<i18n lang="yaml">
  en:
    loading: Loading...
    noOptions: No choices to choose from
  fr:
    loading: Chargement...
    noOptions: Il n'y a pas de choix possible
</i18n>

<template>
  <div class="form-select">
    <label v-if="label" :for="id" class="form__label" v-html="label" />
    <div class="position-relative">
      <input tabindex="-1" class="form-select__value" type="text" :value="model" v-bind="attr" />
      <vue-select
        v-model="model"
        :options="translatedOptions"
        :components="{ OpenIndicator }"
        v-bind="configuration"
      >
        <template v-slot:spinner="{ loading }">
          <span v-if="loading">{{ $t('loading') }}</span>
        </template>
        <template v-slot:no-options>
          <span>{{ $t('noOptions') }}</span>
        </template>
      </vue-select>
    </div>
  </div>
</template>
<script>
import { nanoid } from 'nanoid';
import VueSelect from 'vue-select';

const defaultConfig = {
  /**
   * Reset the components value when the options list changes, based on the
   * following conditions:
   *
   * - The new options list has not the same length than the old one
   * - The new options list does not contain an element with the same value
   * as the one currently selected
   *
   * @param  {Array}   newOptions    The new options list
   * @param  {Array}   oldOptions    The old options list
   * @param  {Object}  selectedValue The currently selected value
   * @return {Boolean}               Whether to reset the current value or not
   */
  resetOnOptionsChange(newOptions, oldOptions, selectedValue) {
    const { value } = selectedValue;
    const hasSelectedValue = newOptions
      .map(option => option.value)
      .includes(value);
    return newOptions.length !== oldOptions.length && !hasSelectedValue;
  },
  /**
   * Disable by default the clearable ability of the select
   * @see  https://vue-select.org/api/props.html#clearable
   * @type {Boolean}
   */
  clearable: false,
};

export default {
  name: 'FormSelect',
  components: {
    VueSelect,
  },
  props: {
    attr: {
      type: Object,
      default: () => {},
    },
    label: {
      type: String,
      default: () => '',
    },
    options: {
      type: Array,
      required: true,
    },
    // eslint-disable-next-line vue/require-prop-types
    value: {
      required: true,
    },
    config: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      id: nanoid(),
      OpenIndicator: {
        render: h => h('span'),
      },
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        // Only emit value if it is different from the curren one
        // or we might get stuck in an infinite loop...
        if (value !== this.value) {
          this.$emit('input', value);
        }
      },
    },
    configuration() {
      const config = Object.assign(
        {},
        defaultConfig,
        {
          inputId: this.id,
          reduce: option => option.value,
        },
        this.config
      );

      if (this.config.placeholder) {
        // eslint-disable-next-line max-len
        config.placeholder =
          this.config.placeholder[this.$i18n.locale] || this.config.placeholder;
      }

      return config;
    },
    translatedOptions() {
      return this.options.map(option => ({
        ...option,
        label: option.label[this.$i18n.locale] || option.label,
      }));
    },
  },
};
</script>
<style lang="scss">
$vs-dropdown-max-height: s(6) * 5;

@import '~vue-select/src/scss/vue-select.scss';

.form-select__value {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  opacity: 0;
}

.v-select {
  position: relative;
}

.vs__selected {
  margin-top: 0;

  .vs--open & {
    height: 100%;
  }
}

.vs__search,
.vs__search:focus {
  margin-top: 0;
  font: inherit;
  text-align: inherit;

  &::placeholder {
    color: currentColor;
    opacity: 0.5;
  }
}

.vs__actions {
  padding: 0;
}

.vs__clear,
.vs__open-indicator {
  display: block;

  svg {
    display: none;
  }
}

.vs__clear {
  position: relative;
  width: 12px;
  height: 12px;
  margin-right: s(1);

  &::after,
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 2px;
    margin-top: -1px;
    background-color: c('blue');
  }

  &::after {
    transform: rotate(45deg);
  }

  &::before {
    transform: rotate(-45deg);
  }
}

.vs__open-indicator {
  width: 10px;
  height: 10px;
  border-top: 2px solid c('green');
  border-right: 2px solid c('green');
  transform: translateY(-25%) rotate(135deg);
  transition: 0.3s transform $out-quad;

  .vs--open & {
    transform: rotate(-45deg);
  }
}

/*------------------------------------*
    List
  *------------------------------------*/
.vs__dropdown-toggle {
  display: flex;
  width: 100%;
  height: 58px;
  padding: 0 s(4);
  font-size: 1em;
  font-weight: 300;
  line-height: 32px;
  color: c('blue');
  cursor: pointer;
  background-color: c('white');
  border: 2px solid c('grey');
  border-radius: 5px;

  &:hover {
    border-color: rgba(c('blue'), 0.25);
  }
}

/*------------------------------------*
    Dropdown
  *------------------------------------*/

.vs__dropdown-menu {
  z-index: l('above-default');
  position: absolute;
  top: calc(100% - 2px);
  left: 0;
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  text-align: inherit;
  background-color: c('white');
  border: 2px solid c('grey');
  border-top: 0;
  border-bottom-right-radius: 5px;
  border-bottom-left-radius: 5px;
  box-shadow: none;

  .vs__dropdown-toggle:hover + & {
    border-color: rgba(c('blue'), 0.25);
  }
}

.vs__no-options,
.vs__dropdown-option {
  z-index: l(default);
  width: auto;
  padding: s(1) s(4);
  line-height: inherit;
  cursor: pointer;

  &::after {
    content: '';
    z-index: l('under-default');
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: calc(100% + 1px);
    background-color: c('grey');
    opacity: 0;
  }

  &.vs__dropdown-option--highlight {
    color: inherit;

    &::after {
      opacity: 1;
    }
  }

  + .vs__dropdown-option {
    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: s(4);
      width: calc(100% - #{s(8)});
      border-top: 1px solid c('grey');
    }
  }
}
</style>
